/* Candell's shipping meter.

   Liquid paints the meter server-side (snippets/candells-shipping-meter.liquid)
   and this repaints it on every cart change. Both must produce identical output
   for the same subtotal — if you change a formula in one, change it in the other.

   Two states only: locked (flat rate) and unlocked (free shipping). The goal
   is always the free-shipping threshold — there is no middle tier and no
   moving goalpost.

   Why a repaint at all, when the drawer is already re-rendered server-side:
   - the cart page's meter sits outside .js-contents, so a quantity change never
     reaches it;
   - the fill can only animate if the node survives the change;
   - removing the last line item leaves a stale meter behind on the cart page.

   No per-node listeners and no stored unsubscriber: meter nodes are destroyed
   and rebuilt by innerHTML replacement on nearly every cart change, so we
   subscribe once and re-query the document each time. */
(function () {
  var METER = '[data-candells-shipping-meter]';

  /* USD only. Every figure the meter prints is a whole dollar by the round-up
     rule, so a general money formatter isn't needed — and the theme has none
     on purpose: every other price is formatted by Liquid's `money` filter. */
  function dollars(amount) {
    return '$' + amount;
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function clampPercent(value) {
    if (!isFinite(value) || value < 0) return 0;
    if (value > 100) return 100;
    return value;
  }

  function setText(el, text) {
    if (el && el.textContent !== text) el.textContent = text;
  }

  function setHidden(el, hidden) {
    if (el) el.hidden = !!hidden;
  }

  /* The headline cross-fades rather than snapping, since it's the one element
     that changes wording rather than width. */
  function setHeadline(el, text) {
    if (!el || el.textContent.trim() === text) return;
    if (prefersReducedMotion()) {
      el.textContent = text;
      return;
    }
    el.style.opacity = '0';
    window.setTimeout(function () {
      el.textContent = text;
      el.style.opacity = '1';
    }, 150);
  }

  /* Remembers the last state per context so the unlock pulse fires on the
     transition into unlocked, not on every re-render of an unlocked cart.
     Module scope on purpose — it has to outlive the node. */
  var lastStates = {};

  function repaintMeter(meter, cart) {
    var threshold = parseFloat(meter.getAttribute('data-threshold'));
    var context = meter.getAttribute('data-context') || 'drawer';

    var subtotal = cart.total_price;
    var thresholdCents = threshold * 100;

    /* Empty cart shows no meter — a 0% bar is a chore, not an invitation. */
    if (!cart.item_count || !subtotal || subtotal <= 0) {
      meter.hidden = true;
      lastStates[context] = 'empty';
      return;
    }
    meter.hidden = false;

    var state = subtotal >= thresholdCents ? 'unlocked' : 'locked';

    var remainingCents = thresholdCents - subtotal;
    /* Round up, always: $12.01 left shows "$13", or the shopper adds $12 and
       misses free shipping. floor(S) + ceil(goal - S) always equals the goal
       for whole-dollar thresholds, so headline and fraction can't contradict. */
    var remaining = Math.ceil(remainingCents / 100);
    var progress = Math.floor(subtotal / 100);

    var headline = state === 'unlocked' ? 'Free shipping unlocked' : dollars(remaining) + ' to free shipping';

    var fill = clampPercent((subtotal / thresholdCents) * 100);

    setHeadline(meter.querySelector('[data-candells-meter-headline]'), headline);

    var fraction = meter.querySelector('[data-candells-meter-fraction]');
    setText(fraction, dollars(progress) + ' / ' + dollars(threshold));
    setHidden(fraction, state === 'unlocked');

    var band = meter.querySelector('[data-candells-meter-band]');
    if (band) band.classList.toggle('is-current', state === 'locked');

    var fillEl = meter.querySelector('[data-candells-meter-fill]');
    if (fillEl) fillEl.style.width = fill + '%';

    var bar = meter.querySelector('[data-candells-meter-bar]');
    if (bar) {
      bar.setAttribute('aria-valuemax', threshold);
      bar.setAttribute('aria-valuenow', progress);
      bar.setAttribute('aria-valuetext', headline);
    }

    meter.classList.toggle('is-locked', state === 'locked');
    meter.classList.toggle('is-unlocked', state === 'unlocked');
    meter.classList.toggle('candells-wobble-a', state === 'unlocked');

    var previous = lastStates[context];
    if (previous && previous !== 'unlocked' && state === 'unlocked' && !prefersReducedMotion()) {
      meter.classList.remove('candells-shipping-meter--pulse');
      void meter.offsetWidth; // force reflow so the animation restarts
      meter.classList.add('candells-shipping-meter--pulse');
    }
    lastStates[context] = state;
  }

  function repaintAll(cart) {
    if (!cart) return;
    var meters = document.querySelectorAll(METER);
    var i;
    for (i = 0; i < meters.length; i++) repaintMeter(meters[i], cart);
  }

  /* The two publishers carry different payloads: product-form.js publishes the
     /cart/add response, which has no total_price and no items; cart.js
     publishes the full /cart/change.js state, which has both. When the payload
     is thin, top up from CartItems.fetchCartData() — a request-deduped
     /cart.json that coalesces with the call product-form.js already makes. */
  function resolveCart(data) {
    var payload = data && data.cartData;
    if (payload && typeof payload.total_price === 'number' && payload.items) {
      return Promise.resolve(payload);
    }
    if (typeof CartItems !== 'undefined' && CartItems.fetchCartData) {
      return CartItems.fetchCartData();
    }
    return fetch(window.routes.cart_url + '.json')
      .then(function (response) {
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
      return resolveCart(data).then(repaintAll);
    });
  }
})();
